// Team Arlo: a friendly, shared game — not a contest. Pure.
//
// "Arlo's day" has five paws, one for each thing a puppy needs every day:
// out to the toilet, fed, rested, a little learning, and something new. Both
// of you fill them together; a day with four or five is a good day, and good
// days in a row make a team streak.
//
// "Fair share" weighs the care each of you logged this week (a night trip
// counts for more than a daytime wee, cleaning up an accident counts too),
// gives each of you a fond title for what you do most, and nudges gently
// when one of you is carrying much more than the other.

import { isExperience } from './visitors.ts';
import { sleepWithin } from './sleep.ts';
import { MINUTE, atClock, dayStart, inClockWindow, startOfDay } from './time.ts';
import type { Household, LogEvent, Millis, Person } from './types.ts';

export type PawId = 'outside' | 'fed' | 'rested' | 'learned' | 'explored';

export interface Paw {
  id: PawId;
  label: string;
  done: boolean;
  /** Where it's at ("2 of 3 sessions", "on pace"). */
  progress: string;
  /** What would fill it — undefined once done. */
  todo?: string;
}

export interface ArloDay {
  date: Millis;
  paws: Paw[];
  filled: number;
  /** Four or five paws. */
  good: boolean;
  perfect: boolean;
}

export interface DayRules {
  /** Meal times for the day ("07:00", …). */
  mealTimes: string[];
  /** Training sessions a day. */
  sessions: number;
}

/** Sleep target: the low end of 15–20 hours a day (Royal Kennel Club; Dogs Trust says 18–20). */
export const SLEEP_HOURS = 15;
/** Toilet trips outside before the paw fills — plenty of chances, not a perfect record. */
const MIN_TRIPS = 3;
const OUTSIDE_SHARE = 0.8;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const hm = (ms: Millis) => {
  const m = Math.max(0, Math.round(ms / MINUTE));
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}`;
};

/** Arlo's five paws for `day` (today: judged on the day so far). */
export function arloDay(events: LogEvent[], day: Millis, now: Millis, rules: DayRules): ArloDay {
  const from = startOfDay(day);
  const to = dayStart(from, 1);
  const until = Math.min(to, now);
  const inDay = events.filter((e) => e.at >= from && e.at < until);
  const count = (pred: (e: LogEvent) => boolean) => inDay.filter(pred).length;

  const outside = count((e) => (e.type === 'wee' || e.type === 'poo') && e.data.where === 'outside');
  const inside = count((e) => (e.type === 'wee' || e.type === 'poo') && e.data.where === 'inside');
  const share = outside + inside ? outside / (outside + inside) : 0;
  const outsideDone = outside >= MIN_TRIPS && share >= OUTSIDE_SHARE;

  const meals = count((e) => e.type === 'meal');
  // Meals whose time (plus a little grace) has come by now.
  const due = Math.max(1, rules.mealTimes.filter((t) => atClock(from, t) + 45 * MINUTE <= until).length);
  const fedDone = meals >= due;

  const slept = sleepWithin(events, from, until);
  // On pace: his share of the day so far, so a morning can fill it too.
  const elapsed = until - from;
  const needSleep = (SLEEP_HOURS / 24) * elapsed;
  const restedDone = elapsed > 0 && slept >= needSleep;

  const sessions = count((e) => e.type === 'training');
  const learnedDone = sessions >= rules.sessions;

  const newThings = count(isExperience);
  const outings = count((e) => e.type === 'walk' && e.data.auto !== 'pending');
  const exploredDone = newThings + outings > 0;

  const paws: Paw[] = [
    {
      id: 'outside',
      label: 'Outside',
      done: outsideDone,
      progress: outside + inside ? `${outside} outside${inside ? ` · ${inside} inside` : ''}` : 'no trips yet',
      todo: outsideDone ? undefined : outside < MIN_TRIPS ? `${plural(MIN_TRIPS - outside, 'more trip')} outside` : 'a few more outside in a row',
    },
    {
      id: 'fed',
      label: 'Fed',
      done: fedDone,
      progress: `${meals} of ${rules.mealTimes.length} meals`,
      todo: fedDone ? undefined : `${plural(due - meals, 'meal')} due by now`,
    },
    {
      id: 'rested',
      label: 'Rested',
      done: restedDone,
      progress: `${hm(slept)} asleep`,
      todo: restedDone ? undefined : `${hm(needSleep - slept)} more sleep${until < to ? ' to be on pace' : ''} (or log the naps)`,
    },
    {
      id: 'learned',
      label: 'Learned',
      done: learnedDone,
      progress: `${sessions} of ${rules.sessions} sessions`,
      todo: learnedDone ? undefined : `${plural(rules.sessions - sessions, 'more five-minute session')}`,
    },
    {
      id: 'explored',
      label: 'Explored',
      done: exploredDone,
      progress: newThings + outings ? [newThings && plural(newThings, 'new thing'), outings && plural(outings, 'outing')].filter(Boolean).join(' · ') : 'nothing new yet',
      todo: exploredDone ? undefined : 'one calm new thing, or a trip out',
    },
  ];
  const filled = paws.filter((p) => p.done).length;
  return { date: from, paws, filled, good: filled >= 4, perfect: filled === 5 };
}

/**
 * Good days in a row, ending yesterday — plus today once it's good (so the
 * streak never "breaks" in the morning before the day's had a chance).
 */
export function teamStreak(events: LogEvent[], now: Millis, rules: DayRules, max = 60): { days: number; today: boolean } {
  const today = arloDay(events, now, now, rules).good;
  let days = 0;
  for (let i = 1; i <= max; i++) {
    const d = dayStart(now, -i);
    if (!arloDay(events, d, now, rules).good) break;
    days++;
  }
  return { days: days + (today ? 1 : 0), today };
}

// --- Fair share ---------------------------------------------------------------------------------

export type CareKind = 'night' | 'toilet' | 'cleanup' | 'meals' | 'training' | 'explore' | 'play' | 'sleep' | 'care';

/** How much each kind of job counts. A 3am trip outside is worth more than a daytime one. */
export const WEIGHTS: Record<CareKind, number> = { night: 3, toilet: 1, cleanup: 2, meals: 1, training: 2, explore: 2, play: 1, sleep: 0.5, care: 1 };

/** A fond title for whoever does the most of each kind. */
export const TITLES: Record<CareKind, string> = {
  night: 'Night owl',
  toilet: 'Wee whisperer',
  cleanup: 'Hero of the mop',
  meals: 'Head chef',
  training: 'Head trainer',
  explore: 'Chief explorer',
  play: 'Chief of fun',
  sleep: 'Sandman',
  care: 'Head nurse',
};

/** What each kind is, counted: "3 night trips". */
export const KIND_LABEL: Record<CareKind, [string, string]> = {
  night: ['night trip', 'night trips'],
  toilet: ['trip outside', 'trips outside'],
  cleanup: ['clean-up', 'clean-ups'],
  meals: ['meal', 'meals'],
  training: ['training session', 'training sessions'],
  explore: ['outing or new thing', 'outings and new things'],
  play: ['game', 'games'],
  sleep: ['nap settled', 'naps settled'],
  care: ['care job', 'care jobs'],
};

/** "3 night trips · 8 meals": what someone did, biggest first (not a score). */
export function didList(byKind: Partial<Record<CareKind, number>>, max = 3): string {
  return (Object.entries(byKind) as [CareKind, number][])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] * WEIGHTS[b[0]] - a[1] * WEIGHTS[a[0]])
    .slice(0, max)
    .map(([k, n]) => `${n} ${KIND_LABEL[k][n === 1 ? 0 : 1]}`)
    .join(' · ');
}

/** What the other one could pick up, for the gentle nudge. */
const NEXT_JOB: Record<CareKind, string> = {
  night: 'tonight’s night trip',
  toilet: 'the next few trips outside',
  cleanup: 'the next clean-up',
  meals: 'the next meal',
  training: 'the next training session',
  explore: 'today’s new thing',
  play: 'the next game',
  sleep: 'settling him for his next nap',
  care: 'the next brush or check-over',
};

export function careKind(e: LogEvent, night: Pick<Household, 'nightStart' | 'nightEnd'>): CareKind | undefined {
  // Logged by the camera or an automation: nobody did that by hand.
  if (e.source === 'automation') return undefined;
  switch (e.type) {
    case 'wee':
    case 'poo':
      if (e.data.where === 'inside') return 'cleanup';
      return inClockWindow(e.at, night.nightStart, night.nightEnd) ? 'night' : 'toilet';
    case 'meal':
      return 'meals';
    case 'training':
      return 'training';
    case 'social':
    case 'walk':
      return 'explore';
    case 'play':
      return 'play';
    case 'sleep':
      return 'sleep';
    case 'groom':
    case 'meds':
    case 'health':
    case 'care':
    case 'weight':
      return 'care';
    default:
      return undefined;
  }
}

export interface Share {
  person: Person;
  points: number;
  /** 0–1 of everyone's points. */
  share: number;
  byKind: Partial<Record<CareKind, number>>;
  title?: string;
}

export interface FairShare {
  shares: Share[];
  total: number;
  /** "Nicely shared", or a gentle nudge. */
  note?: { tone: 'good' | 'info'; text: string };
}

/** Who logged what over [from, to], weighted, with titles and a nudge. */
export function fairShare(events: LogEvent[], people: Person[], household: Pick<Household, 'nightStart' | 'nightEnd'>, from: Millis, to: Millis): FairShare {
  const shares: Share[] = people.map((person) => ({ person, points: 0, share: 0, byKind: {} }));
  for (const e of events) {
    if (e.at < from || e.at >= to || !e.by) continue;
    const s = shares.find((x) => x.person.id === e.by);
    const kind = careKind(e, household);
    if (!s || !kind) continue;
    s.byKind[kind] = (s.byKind[kind] ?? 0) + 1;
    s.points += WEIGHTS[kind];
  }
  const total = shares.reduce((n, s) => n + s.points, 0);
  for (const s of shares) s.share = total ? s.points / total : 0;

  // Titles: each kind goes to whoever did the most of it; everyone gets their best
  // (the kind where their lead is biggest), and no two people get the same one.
  const taken = new Set<CareKind>();
  const lead = (s: Share, k: CareKind) => {
    const mine = s.byKind[k] ?? 0;
    const all = shares.reduce((n, x) => n + (x.byKind[k] ?? 0), 0);
    return mine >= 2 && all ? (mine / all) * WEIGHTS[k] * Math.log2(1 + mine) : 0;
  };
  for (const s of [...shares].sort((a, b) => b.points - a.points)) {
    const best = (Object.keys(WEIGHTS) as CareKind[]).filter((k) => !taken.has(k) && lead(s, k) > 0).sort((a, b) => lead(s, b) - lead(s, a))[0];
    if (best) {
      taken.add(best);
      s.title = TITLES[best];
    }
  }

  let note: FairShare['note'];
  const active = shares.filter((s) => s.points > 0 || shares.length === 2);
  if (active.length >= 2 && total >= 15) {
    const top = [...shares].sort((a, b) => b.share - a.share)[0];
    const fair = 1 / shares.length;
    if (top.share >= fair + 0.15) {
      const others = shares.filter((s) => s !== top).map((s) => s.person.name);
      // The kind of job they're carrying most of, for the other person to pick up.
      const kinds = (Object.keys(WEIGHTS) as CareKind[]).filter((k) => (top.byKind[k] ?? 0) > 0);
      const heavy = kinds.sort((a, b) => (top.byKind[b] ?? 0) * WEIGHTS[b] - (top.byKind[a] ?? 0) * WEIGHTS[a])[0];
      // A swap, not a score: the one kind of job to share, and who might take the next one.
      note = {
        tone: 'info',
        text: heavy ? `${top.person.name}’s done most of the ${KIND_LABEL[heavy][1]} this week — ${others.join(' or ')}, fancy ${NEXT_JOB[heavy]}?` : `${others.join(' or ')}, fancy the next job?`,
      };
    } else note = { tone: 'good', text: 'Nicely shared this week — real teamwork.' };
  }
  return { shares, total, note };
}

/** The week so far: from seven days ago to now. */
export const weekFrom = (now: Millis) => dayStart(now, -6);

